import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SocialShareButtons } from '@/components/public/SocialShareButtons';
import type {
  ClassroomPublicConfig,
  ClassroomTopMomentsConfig,
  ClassroomThemeConfig,
  ClassroomQuote,
  ClassroomWishlistItem,
  ClassroomVisibility,
} from '@/types/classrooms';

const PLACEHOLDER_AVATAR = '/teacher-placeholder.svg';

interface ClassroomData {
  id: string;
  name: string;
  grade_level?: string | null;
  displayName: string;
  about?: string | null;
  quote?: ClassroomQuote | null;
  goals: string[];
  wishlist: ClassroomWishlistItem[];
  topMoments?: ClassroomTopMomentsConfig | null;
  theme?: ClassroomThemeConfig;
}

interface ShareItem {
  display_name: string;
  avatar_url?: string | null;
  school?: string | null;
  city?: string | null;
  state?: string | null;
  bio?: string | null;
  classroom?: ClassroomData | null;
  visibility?: ClassroomVisibility | null;
  share_code?: string | null;
}

type LoadStatus = 'loading' | 'ready' | 'error';

const themeStyles: Record<string, { bg: string; accent: string; text: string }> = {
  clean: { bg: 'bg-white', accent: 'bg-blue-600', text: 'text-gray-900' },
  playful: { bg: 'bg-gradient-to-br from-amber-50 to-rose-50', accent: 'bg-rose-500', text: 'text-gray-800' },
  bold: { bg: 'bg-gray-900', accent: 'bg-amber-500', text: 'text-white' },
  minimal: { bg: 'bg-gray-50', accent: 'bg-gray-800', text: 'text-gray-700' },
};

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

export default function TeacherShare() {
  const { code } = useParams();
  const [item, setItem] = useState<ShareItem | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setErrorMessage('');
    setItem(null);

    const fetchShare = async () => {
      try {
        const url = `${CORE_API}/api/v1/community/share-resolve?code=${encodeURIComponent(code || '')}`;
        const response = await fetch(url);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.error || 'Share link not found');
        }
        if (!cancelled) {
          setItem(data?.item || null);
          setStatus('ready');
        }
      } catch (error) {
        if (!cancelled) {
          setStatus('error');
          setErrorMessage(error instanceof Error ? error.message : 'Share link not found');
        }
      }
    };

    if (code) {
      fetchShare();
    } else {
      setStatus('error');
      setErrorMessage('Share link not found');
    }

    return () => {
      cancelled = true;
    };
  }, [code]);

  const locationDisplay = useMemo(() => {
    if (!item) return '';
    const parts = [];
    if (item.school) parts.push(item.school);
    const cityState = [item.city, item.state].filter(Boolean).join(', ');
    if (cityState) parts.push(cityState);
    return parts.join(' • ');
  }, [item]);

  const sponsorPath = useMemo(() => {
    if (!code) return '/sponsor/signup';
    const params = new URLSearchParams({ share: code });
    return `/sponsor/signup?${params.toString()}`;
  }, [code]);

  const donateSharePath = useMemo(() => {
    if (!code) return '/donate';
    const params = new URLSearchParams({ share: code });
    return `/donate?${params.toString()}`;
  }, [code]);

  const walletSharePath = useMemo(() => {
    if (!code) return '/sponsor/wallet';
    const params = new URLSearchParams({ share: code });
    return `/sponsor/wallet?${params.toString()}`;
  }, [code]);

  const shareUrl = useMemo(() => {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://projecttower.org';
    return `${base}/t/${code}`;
  }, [code]);

  const classroom = item?.classroom;
  const visibility = item?.visibility;
  const theme = classroom?.theme;
  const style = themeStyles[theme?.style || 'clean'] || themeStyles.clean;
  const accentColor = theme?.accentColor || '#3b82f6';
  const layout = theme?.layout || 'classic';

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-shell/60 p-6">
        <div className="mx-auto max-w-2xl rounded-3xl bg-white/80 p-8 text-center text-cocoa/70 shadow-soft">
          Loading classroom…
        </div>
      </div>
    );
  }

  if (status === 'error' || !item) {
    return (
      <div className="min-h-screen bg-shell/60 p-6">
        <div className="mx-auto max-w-2xl rounded-3xl bg-white/80 p-8 text-center text-cocoa/70 shadow-soft">
          {errorMessage || 'Link not found or no longer active.'}
        </div>
      </div>
    );
  }

  const avatarSrc = item.avatar_url || PLACEHOLDER_AVATAR;
  const classroomName = classroom?.displayName || classroom?.name || `${item.display_name}'s Classroom`;

  // If no classroom data (visibility not enabled), show basic teacher info
  if (!classroom) {
    return (
      <div className="min-h-screen bg-shell p-6">
        <div className="mx-auto max-w-3xl space-y-6 rounded-3xl bg-white p-6 shadow-soft">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <img
              src={avatarSrc}
              alt={item.display_name || 'Teacher avatar'}
              className="h-24 w-24 rounded-full border border-sand object-cover"
              onError={(event) => {
                if (event.currentTarget.src !== PLACEHOLDER_AVATAR) {
                  event.currentTarget.src = PLACEHOLDER_AVATAR;
                }
              }}
            />
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-cocoa/60">Project Tower Teacher</p>
              <h1 className="text-3xl font-semibold text-cocoa">{item.display_name}</h1>
              {locationDisplay ? <p className="text-sm text-cocoa/70">{locationDisplay}</p> : null}
            </div>
          </header>

          {item.bio ? <p className="text-base leading-relaxed text-cocoa/85">{item.bio}</p> : null}

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              className="btn flex-1"
              onClick={() => {
                window.location.href = sponsorPath;
              }}
            >
              Sponsor this Teacher
            </button>
            <Link to="/donate" className="btn flex-1 bg-white text-cocoa hover:bg-shell">
              Make a Donation
            </Link>
          </div>

          <div className="rounded-xl border border-sand bg-shell/30 p-4 text-xs text-cocoa/60">
            Share code: <span className="font-mono text-cocoa/80">{code}</span>
          </div>
        </div>
      </div>
    );
  }

  // Rich classroom display
  return (
    <div className={`min-h-screen ${style.bg} p-4 md:p-8`}>
      <div className="mx-auto max-w-4xl space-y-8">
        {/* Header with classroom name */}
        <header
          className="rounded-3xl p-8 text-center shadow-lg"
          style={{ backgroundColor: accentColor + '15', borderColor: accentColor }}
        >
          <div className="mb-4 flex justify-center">
            <img
              src={avatarSrc}
              alt={item.display_name || 'Teacher avatar'}
              className="h-20 w-20 rounded-full border-4 border-white object-cover shadow-md"
              onError={(event) => {
                if (event.currentTarget.src !== PLACEHOLDER_AVATAR) {
                  event.currentTarget.src = PLACEHOLDER_AVATAR;
                }
              }}
            />
          </div>
          <h1 className={`text-3xl font-bold ${style.text} md:text-4xl`}>{classroomName}</h1>
          <p className="mt-2 text-sm text-cocoa/70">
            {item.display_name} {locationDisplay ? `• ${locationDisplay}` : ''}
          </p>
          {classroom.grade_level && (
            <span
              className="mt-3 inline-block rounded-full px-3 py-1 text-xs font-medium text-white"
              style={{ backgroundColor: accentColor }}
            >
              {classroom.grade_level}
            </span>
          )}
        </header>

        {/* Quote Section */}
        {classroom.quote?.text && (
          <section className="rounded-2xl bg-white/80 p-6 shadow-soft">
            <blockquote className="text-center">
              <p className={`text-xl italic ${style.text} md:text-2xl`}>"{classroom.quote.text}"</p>
              {classroom.quote.attribution && (
                <footer className="mt-3 text-sm text-cocoa/60">— {classroom.quote.attribution}</footer>
              )}
            </blockquote>
          </section>
        )}

        {/* About Section */}
        {classroom.about && (
          <section className="rounded-2xl bg-white p-6 shadow-soft">
            <h2 className={`mb-3 text-lg font-semibold ${style.text}`}>About Our Classroom</h2>
            <p className="leading-relaxed text-cocoa/85">{classroom.about}</p>
          </section>
        )}

        {/* Goals Section */}
        {classroom.goals.length > 0 && (
          <section className="rounded-2xl bg-white p-6 shadow-soft">
            <h2 className={`mb-4 text-lg font-semibold ${style.text}`}>Our Goals This Year</h2>
            <ul className="space-y-3">
              {classroom.goals.map((goal, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span
                    className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: accentColor }}
                  >
                    {idx + 1}
                  </span>
                  <span className="text-cocoa/85">{goal}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Top Moments Section */}
        {classroom.topMoments?.items && classroom.topMoments.items.length > 0 && (
          <section className="rounded-2xl bg-white p-6 shadow-soft">
            <h2 className={`mb-4 text-lg font-semibold ${style.text}`}>Top Moments</h2>
            <div
              className={
                layout === 'pinboard'
                  ? 'columns-1 gap-4 sm:columns-2 lg:columns-3'
                  : layout === 'timeline'
                    ? 'relative border-l-2 pl-6'
                    : layout === 'collage'
                      ? 'grid auto-rows-[minmax(120px,auto)] gap-3 sm:grid-cols-3'
                      : layout === 'storybook'
                        ? 'space-y-4'
                        : layout === 'interactive'
                          ? 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3'
                          : 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3'
              }
              style={layout === 'timeline' ? { borderColor: accentColor } : undefined}
            >
              {classroom.topMoments.items
                .sort((a, b) => (a.position || 0) - (b.position || 0))
                .map((moment, idx) => {
                  // Collage layout: vary sizes based on position
                  const collageSpan =
                    layout === 'collage'
                      ? idx % 5 === 0
                        ? 'sm:col-span-2 sm:row-span-2'
                        : idx % 3 === 0
                          ? 'sm:col-span-2'
                          : ''
                      : '';

                  // Storybook layout: alternating page styling
                  const storybookStyle =
                    layout === 'storybook'
                      ? idx % 2 === 0
                        ? 'border-l-4 pl-4'
                        : 'border-r-4 pr-4 text-right'
                      : '';

                  return (
                    <div
                      key={moment.id}
                      className={
                        layout === 'pinboard'
                          ? 'mb-4 break-inside-avoid rounded-xl border bg-shell/30 p-4'
                          : layout === 'timeline'
                            ? 'relative mb-6 rounded-xl border bg-shell/30 p-4'
                            : layout === 'collage'
                              ? `flex flex-col justify-center rounded-xl border bg-shell/30 p-4 ${collageSpan}`
                              : layout === 'storybook'
                                ? `rounded-xl border bg-shell/30 p-5 ${storybookStyle}`
                                : layout === 'interactive'
                                  ? 'rounded-xl border bg-shell/30 p-4 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-opacity-60'
                                  : 'rounded-xl border bg-shell/30 p-4'
                      }
                      style={
                        layout === 'storybook'
                          ? { borderColor: accentColor }
                          : layout === 'interactive'
                            ? { borderColor: accentColor + '40' }
                            : undefined
                      }
                    >
                      {layout === 'timeline' && (
                        <div
                          className="absolute -left-9 top-4 h-4 w-4 rounded-full border-4 border-white"
                          style={{ backgroundColor: accentColor }}
                        />
                      )}
                      {layout === 'storybook' && (
                        <span
                          className="mb-2 inline-block text-3xl font-serif opacity-30"
                          style={{ color: accentColor }}
                        >
                          {idx + 1}
                        </span>
                      )}
                      <h3 className="font-semibold text-cocoa">{moment.title}</h3>
                      {moment.date && <p className="mt-1 text-xs text-cocoa/60">{moment.date}</p>}
                      {moment.summary && <p className="mt-2 text-sm text-cocoa/75">{moment.summary}</p>}
                    </div>
                  );
                })}
            </div>
          </section>
        )}

        {/* Wishlist Section */}
        {classroom.wishlist.length > 0 && (
          <section className="rounded-2xl bg-white p-6 shadow-soft">
            <h2 className={`mb-4 text-lg font-semibold ${style.text}`}>Classroom Wishlist</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {classroom.wishlist.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 rounded-xl border bg-shell/30 p-4"
                >
                  <span
                    className="flex h-8 w-8 items-center justify-center rounded-full text-white"
                    style={{ backgroundColor: accentColor }}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-cocoa">{item.label}</p>
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs hover:underline"
                        style={{ color: accentColor }}
                      >
                        View item →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Support Section - only if showShareLink is enabled */}
        {visibility?.showShareLink && (
          <section className="rounded-2xl bg-gradient-to-r from-amber-100 via-rose-50 to-amber-100 p-6 shadow-soft">
            <h2 className="mb-4 text-center text-xl font-semibold text-cocoa">Support This Classroom</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3 rounded-2xl bg-white p-4">
                <h3 className="text-lg font-semibold text-cocoa">Send a cash donation</h3>
                <p className="text-sm text-cocoa/70">
                  Cash gifts use secure Stripe checkouts and go straight to the teacher's classroom fund.
                </p>
                <Link
                  to={donateSharePath}
                  className="inline-flex w-full justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-soft"
                  style={{ backgroundColor: accentColor }}
                >
                  Donate cash
                </Link>
              </div>
              <div className="space-y-3 rounded-2xl bg-white p-4">
                <h3 className="text-lg font-semibold text-cocoa">Gift Tower credits</h3>
                <p className="text-sm text-cocoa/70">
                  Platform credits power keepsakes and emblems. Sign in as a sponsor to send credits.
                </p>
                <Link
                  to={walletSharePath}
                  className="inline-flex w-full justify-center rounded-xl border px-4 py-2 text-sm font-semibold shadow-soft"
                  style={{ borderColor: accentColor, color: accentColor }}
                >
                  Gift credits
                </Link>
              </div>
            </div>
            <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  window.location.href = sponsorPath;
                }}
              >
                Become a Sponsor
              </button>
            </div>

            {item.share_code && (
              <div className="mt-4 rounded-xl border border-sand bg-white/60 p-3 text-center text-xs text-cocoa/60">
                Share code: <span className="font-mono text-cocoa/80">{item.share_code}</span>
              </div>
            )}
          </section>
        )}

        {/* Social Share Section */}
        <section className="rounded-2xl bg-white p-6 text-center shadow-soft">
          <h2 className="mb-4 text-lg font-semibold text-cocoa">Share This Classroom</h2>
          <p className="mb-4 text-sm text-cocoa/70">
            Help spread the word about {classroomName}!
          </p>
          <SocialShareButtons
            url={shareUrl}
            title={`Check out ${classroomName} on Project Tower!`}
            description={classroom.about || `Support ${item.display_name}'s classroom on Project Tower.`}
          />
        </section>

        {/* Footer */}
        <footer className="py-4 text-center text-xs text-cocoa/50">
          <p>
            Powered by{' '}
            <a href="/" className="underline hover:text-cocoa/70">
              Project Tower
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
